import React from 'react';
import { withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import moment from 'moment';

const styles = (theme) => ({
  dialogPaper: {
    minWidth: 750,
    maxWidth: 900,
    maxHeight: '90vh',
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(90deg, rgba(37,99,235,0.95), rgba(56,189,248,0.95))',
    color: '#ffffff',
  },
  titleText: {
    fontSize: 16,
    fontWeight: 600,
  },
  subtitleText: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.9)',
    marginTop: 2,
  },
  dialogContent: {
    padding: theme.spacing(2),
    overflowY: 'auto',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: '2px solid #e5e7eb',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: '1px solid #f1f5f9',
  },
  infoLabel: {
    fontWeight: 500,
    color: '#64748b',
    fontSize: 13,
  },
  infoValue: {
    fontWeight: 600,
    color: '#1e293b',
    fontSize: 13,
    textAlign: 'right',
  },
  metricCard: {
    padding: theme.spacing(1.5),
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc',
    marginBottom: theme.spacing(1),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    // minHeight: 140,
  },
  metricLabel: {
    fontSize: 11,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#059669',
  },
  breakdownGrid: {
    marginTop: theme.spacing(0.5),
  },
  breakdownItem: {
    padding: theme.spacing(1),
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    marginBottom: theme.spacing(0.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 140,
  },
  breakdownTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: theme.spacing(0.5),
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
    fontSize: 12,
  },
  breakdownLabel: {
    color: '#64748b',
  },
  breakdownValue: {
    fontWeight: 600,
    color: '#1e293b',
  },
  jsonView: {
    padding: theme.spacing(1.5),
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    border: '1px solid #e5e7eb',
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 300,
    overflowY: 'auto',
  },
  closeButton: {
    color: '#fecaca',
    '&:hover': {
      color: '#fee2e2',
      backgroundColor: 'rgba(248, 113, 113, 0.25)',
    },
  },
});

const InvoiceDetailDialog = ({ classes, open, invoice, onClose }) => {
  if (!invoice) return null;

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 10,
      useGrouping: true,
    }).format(num);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Number(value) || 0);
  };

  const invoiceData = invoice.invoice_data || {};
  let costBreakdown = invoiceData.costBreakdown || {};
  
  // Calculate cost breakdown from invoice totals if not available
  if (!costBreakdown || Object.keys(costBreakdown).length === 0 || 
      (!costBreakdown.sms && !costBreakdown.mms)) {
    const totalMessages = invoice.total_messages || invoice.totalMessages || 0;
    const totalSegments = invoice.total_segments || invoice.totalSegments || 0;
    const totalSpend = invoice.total_monthly_spend || invoice.totalMonthlySpend || 0;
    
    // Try to get SMS/MMS breakdown from invoice_data messages if available
    const messages = invoiceData.messages || [];
    let smsCount = 0;
    let smsSegments = 0;
    let smsSpend = 0;
    let mmsCount = 0;
    let mmsSegments = 0;
    let mmsSpend = 0;
    
    if (messages.length > 0) {
      messages.forEach((msg) => {
        const segments = msg.segments || msg.numSegments || 1;
        const cost = Math.abs(msg.price || msg.cost || 0);
        const isMMS = (msg.numMedia || 0) > 0 || 
                     (msg.carrier && msg.carrier.toLowerCase() === 'mms') ||
                     (msg.messageType && msg.messageType.toLowerCase() === 'mms');
        
        if (isMMS) {
          mmsCount += 1;
          mmsSegments += segments;
          mmsSpend += cost;
        } else {
          smsCount += 1;
          smsSegments += segments;
          smsSpend += cost;
        }
      });
    }
    
    // If we calculated from messages, use that; otherwise use totals
    if (smsCount === 0 && mmsCount === 0 && totalMessages > 0) {
      // Default to showing all as SMS if we can't determine
      smsCount = totalMessages;
      smsSegments = totalSegments;
      smsSpend = totalSpend;
    }
    
    // Merge with existing costBreakdown or create new one
    costBreakdown = {
      ...costBreakdown,
      sms: costBreakdown.sms || {
        count: smsCount || 0,
        segments: smsSegments || 0,
        spend: smsSpend || 0,
      },
      mms: costBreakdown.mms || {
        count: mmsCount || 0,
        segments: mmsSegments || 0,
        spend: mmsSpend || 0,
      },
    };
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div>
          <div className={classes.titleText}>
            Invoice {invoice.invoice_number || invoice.invoiceNumber}
          </div>
          <div className={classes.subtitleText}>
            {moment(invoice.invoice_date || invoice.invoiceDate).format(
              'MMMM YYYY'
            )}
          </div>
        </div>
        <IconButton
          onClick={onClose}
          size="small"
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Invoice Information */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Invoice Information
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>Invoice Details</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Invoice Number:</span>
                  <span className={classes.breakdownValue}>
                    {invoice.invoice_number || invoice.invoiceNumber}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Invoice Month:</span>
                  <span className={classes.breakdownValue}>
                    {invoice.invoice_month || invoice.invoiceMonth}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Invoice Date:</span>
                  <span className={classes.breakdownValue}>
                    {moment(invoice.invoice_date || invoice.invoiceDate).format(
                      'MMMM D, YYYY'
                    )}
                  </span>
                </div>
                {invoice.due_date && (
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Due Date:</span>
                    <span className={classes.breakdownValue}>
                      {moment(invoice.due_date).format('MMMM D, YYYY')}
                    </span>
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>Account Details</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Account Name:</span>
                  <span className={classes.breakdownValue}>
                    {(invoice.account && invoice.account.name) ||
                      invoice.accountName ||
                      invoice.store_name ||
                      invoice.storeName ||
                      'N/A'}
                  </span>
                </div>
                {invoice.warehouse_no && (
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Warehouse #:</span>
                    <span className={classes.breakdownValue}>
                      {invoice.warehouse_no}
                    </span>
                  </div>
                )}
                {invoice.location_name && (
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Location:</span>
                    <span className={classes.breakdownValue}>
                      {invoice.location_name}
                    </span>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Financial Summary */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Financial Summary
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <Paper className={classes.metricCard}>
                <div className={classes.metricLabel}>Total Sales</div>
                <div className={classes.metricValue}>
                  {formatCurrency(invoice.total_sales || invoice.totalSales)}
                </div>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper className={classes.metricCard}>
                <div className={classes.metricLabel}>Total Tax</div>
                <div className={classes.metricValue}>
                  {formatCurrency(invoice.total_tax || invoice.totalTax || 0)}
                </div>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper className={classes.metricCard}>
                <div className={classes.metricLabel}>Total Monthly Spend</div>
                <div className={classes.metricValue}>
                  {formatCurrency(
                    invoice.total_monthly_spend || invoice.totalMonthlySpend
                  )}
                </div>
              </Paper>
            </Grid>
          </Grid>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Message Summary */}
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Message Summary
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>Total Messages</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Count:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.total_messages || invoice.totalMessages
                    )}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Segments:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.total_segments || invoice.totalSegments
                    )}
                  </span>
                </div>
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>Inbound Messages</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Count:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.inbound_messages || invoice.inboundMessages
                    )}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Segments:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.inbound_segments || invoice.inboundSegments
                    )}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Cost:</span>
                  <span className={classes.breakdownValue}>
                    {formatCurrency(invoice.inbound_cost || invoice.inboundCost)}
                  </span>
                </div>
                {invoice.inbound_failed_messages > 0 && (
                  <React.Fragment>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>
                        Failed Messages:
                      </span>
                      <span className={classes.breakdownValue}>
                        {formatNumber(
                          invoice.inbound_failed_messages ||
                            invoice.inboundFailedMessages
                        )}
                      </span>
                    </div>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>
                        Failed Cost:
                      </span>
                      <span className={classes.breakdownValue}>
                        {formatCurrency(
                          invoice.inbound_failed_cost ||
                            invoice.inboundFailedCost
                        )}
                      </span>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>Outbound Messages</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Count:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.outbound_messages || invoice.outboundMessages
                    )}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Segments:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber(
                      invoice.outbound_segments || invoice.outboundSegments
                    )}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Cost:</span>
                  <span className={classes.breakdownValue}>
                    {formatCurrency(
                      invoice.outbound_cost || invoice.outboundCost
                    )}
                  </span>
                </div>
                {invoice.outbound_failed_messages > 0 && (
                  <React.Fragment>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>
                        Failed Messages:
                      </span>
                      <span className={classes.breakdownValue}>
                        {formatNumber(
                          invoice.outbound_failed_messages ||
                            invoice.outboundFailedMessages
                        )}
                      </span>
                    </div>
                    <div className={classes.breakdownRow}>
                      <span className={classes.breakdownLabel}>
                        Failed Cost:
                      </span>
                      <span className={classes.breakdownValue}>
                        {formatCurrency(
                          invoice.outbound_failed_cost ||
                            invoice.outboundFailedCost
                        )}
                      </span>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </Grid>
          </Grid>
        </div>

        {/* Cost Breakdown */}
        <Divider style={{ margin: '12px 0' }} />
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Cost Breakdown
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>SMS</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Count:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber((costBreakdown.sms && costBreakdown.sms.count) || 0)}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Segments:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber((costBreakdown.sms && costBreakdown.sms.segments) || 0)}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Spend:</span>
                  <span className={classes.breakdownValue}>
                    {formatCurrency((costBreakdown.sms && costBreakdown.sms.spend) || 0)}
                  </span>
                </div>
              </div>
            </Grid>
            <Grid item xs={4}>
              <div className={classes.breakdownItem}>
                <div className={classes.breakdownTitle}>MMS</div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Count:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber((costBreakdown.mms && costBreakdown.mms.count) || 0)}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Segments:</span>
                  <span className={classes.breakdownValue}>
                    {formatNumber((costBreakdown.mms && costBreakdown.mms.segments) || 0)}
                  </span>
                </div>
                <div className={classes.breakdownRow}>
                  <span className={classes.breakdownLabel}>Spend:</span>
                  <span className={classes.breakdownValue}>
                    {formatCurrency((costBreakdown.mms && costBreakdown.mms.spend) || 0)}
                  </span>
                </div>
              </div>
            </Grid>
            {costBreakdown.failed && (costBreakdown.failed.count > 0 || costBreakdown.failed.spend > 0) && (
              <Grid item xs={4}>
                <div className={classes.breakdownItem}>
                  <div className={classes.breakdownTitle}>Failed</div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Count:</span>
                    <span className={classes.breakdownValue}>
                      {formatNumber(costBreakdown.failed.count || 0)}
                    </span>
                  </div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Segments:</span>
                    <span className={classes.breakdownValue}>
                      {formatNumber(costBreakdown.failed.segments || 0)}
                    </span>
                  </div>
                  <div className={classes.breakdownRow}>
                    <span className={classes.breakdownLabel}>Spend:</span>
                    <span className={classes.breakdownValue}>
                      {formatCurrency(costBreakdown.failed.spend || 0)}
                    </span>
                  </div>
                </div>
              </Grid>
            )}
          </Grid>
        </div>

        {/* Processed Status */}
        {invoice.is_processed !== undefined && (
          <React.Fragment>
            <Divider style={{ margin: '12px 0' }} />
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Status</Typography>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Processed:</span>
                <span className={classes.infoValue}>
                  {invoice.is_processed ? 'Yes' : 'No'}
                </span>
              </div>
              {invoice.processed_at && (
                <div className={classes.infoRow}>
                  <span className={classes.infoLabel}>Processed At:</span>
                  <span className={classes.infoValue}>
                    {moment(invoice.processed_at).format(
                      'MMMM D, YYYY [at] h:mm A'
                    )}
                  </span>
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(InvoiceDetailDialog);




// WEBPACK FOOTER //
// ./src/components/billing/InvoiceDetailDialog.js